import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { HexdiLogo } from '@/components/HexdiLogo';
import { NotificationsBell } from '@/components/NotificationsBell';
import { SearchBar } from '@/components/SearchBar';
import { CreateCompanyScreen } from '@/components/CreateCompanyScreen';
import { useLogout, useMe } from '@/lib/auth';
import {
  setActiveCompanyId,
  useActiveCompanyId,
} from '@/lib/active-company';
import { useMyCompanies, useSubscription } from '@/lib/companies';

type NavLinkDef = {
  to: string;
  label: string;
  isActive?: (pathname: string) => boolean;
};

const NAV_LINKS: NavLinkDef[] = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    isActive: (p) => p === '/dashboard' || p.startsWith('/module'),
  },
  { to: '/report', label: 'Reports' },
  { to: '/settings/account', label: 'Settings' },
];

export function AppLayout() {
  const navigate = useNavigate();
  const me = useMe();
  const logout = useLogout();
  const companies = useMyCompanies();
  const companyId = useActiveCompanyId();
  const subscription = useSubscription(companyId);

  // The user is authenticated but belongs to no company. Block the app behind a
  // create-company screen until they make one — this also prevents reaching the
  // team/invite flow before a company exists.
  const hasNoCompany =
    companies.isSuccess && (companies.data?.length ?? 0) === 0;

  useEffect(() => {
    if (me.isError || me.data === null) {
      navigate('/login', { replace: true });
    }
  }, [me.isError, me.data, navigate]);

  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-body text-text-secondary">Loading…</p>
      </div>
    );
  }

  if (!me.data) return null;

  const handleSignOut = () => {
    logout.mutate(undefined, {
      onSuccess: () => navigate('/login', { replace: true }),
    });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <TopNav user={me.data} onSignOut={handleSignOut} />

      {subscription.data?.isReadOnly ? (
        <SubscriptionBanner>
          Your subscription is past its grace period — the app is in read-only
          mode.{' '}
          <Link to="/settings/subscription" className="font-medium underline">
            Reactivate subscription
          </Link>
          .
        </SubscriptionBanner>
      ) : subscription.data?.status === 'PAST_DUE' ? (
        <SubscriptionBanner>
          Your last payment failed.{' '}
          <Link to="/settings/subscription" className="font-medium underline">
            Update payment method
          </Link>
          .
        </SubscriptionBanner>
      ) : null}

      <main className="flex-1">
        {hasNoCompany ? <CreateCompanyScreen /> : <Outlet />}
      </main>
    </div>
  );
}

function SubscriptionBanner({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-b border-warning/40 bg-warning/10 px-5 py-3 text-center text-body-sm text-warning md:px-10 lg:px-20">
      {children}
    </div>
  );
}

type TopNavProps = {
  user: { name: string | null; email: string; roles?: string[] };
  onSignOut: () => void;
};

function TopNav({ user, onSignOut }: TopNavProps) {
  const location = useLocation();

  const links = user.roles?.includes('admin')
    ? [
        ...NAV_LINKS,
        {
          to: '/admin/review',
          label: 'Admin',
          isActive: (p: string) => p.startsWith('/admin'),
        },
      ]
    : NAV_LINKS;

  return (
    <header className="border-b border-border bg-surface">
      <div className="mx-auto flex h-[68px] max-w-content-wide items-center justify-between gap-6 px-5 md:px-10 lg:px-20">
        <div className="flex items-center gap-10">
          <Link to="/dashboard" aria-label="HEXDI home">
            <HexdiLogo />
          </Link>
          <nav aria-label="Primary">
            <ul className="hidden items-center gap-6 md:flex">
              {links.map((link) => {
                const active = link.isActive
                  ? link.isActive(location.pathname)
                  : location.pathname.startsWith(link.to);
                return (
                  <li key={link.label}>
                    <NavLink
                      to={link.to}
                      end={link.to === '/dashboard' && link.label === 'Dashboard'}
                      className={cn(
                        'inline-flex h-9 items-center text-body-sm transition-colors',
                        active
                          ? 'font-semibold text-accent'
                          : 'text-text-secondary hover:text-text-primary',
                      )}
                    >
                      {link.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <SearchBar />
          <NotificationsBell />
          <UserMenu user={user} onSignOut={onSignOut} />
        </div>
      </div>
    </header>
  );
}

function UserMenu({ user, onSignOut }: TopNavProps) {
  const companies = useMyCompanies();
  const activeId = useActiveCompanyId();
  const qc = useQueryClient();

  const activeCompany =
    companies.data?.find((c) => c.id === activeId) ?? companies.data?.[0];
  const initials = getInitials(user.name ?? user.email);
  const displayName = user.name ?? user.email;

  const handleSwitchCompany = (id: number) => {
    setActiveCompanyId(id);
    qc.invalidateQueries();
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="group flex items-center gap-3 rounded-md px-1 py-1 transition-colors hover:bg-background focus-visible:bg-background"
          aria-label="Account menu"
        >
          <span className="hidden flex-col items-end gap-0.5 md:flex">
            <span className="text-[13px] font-medium leading-none text-text-primary">
              {displayName}
            </span>
            {activeCompany ? (
              <span className="text-[11px] leading-none text-text-secondary">
                {activeCompany.name ?? 'Untitled company'}
              </span>
            ) : null}
          </span>
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-text-primary text-body-sm font-semibold text-white">
            {initials}
          </span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="min-w-60 rounded-lg border border-border bg-surface p-1 shadow-card-hover"
        >
          <div className="px-3 pb-2 pt-2">
            <p className="text-body-sm font-medium text-text-primary">
              {displayName}
            </p>
            <p className="text-caption text-text-secondary">{user.email}</p>
          </div>

          {companies.data && companies.data.length > 1 ? (
            <>
              <DropdownMenu.Separator className="mx-2 my-1 h-px bg-border" />
              <DropdownMenu.Label className="px-3 pb-1 pt-2 text-label uppercase tracking-[0.08em] text-text-secondary">
                Switch company
              </DropdownMenu.Label>
              {companies.data.map((c) => (
                <DropdownMenu.Item
                  key={c.id}
                  onSelect={() => handleSwitchCompany(c.id)}
                  className={cn(
                    'flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-body-sm outline-none data-[highlighted]:bg-background',
                    c.id === activeId && 'text-accent',
                  )}
                >
                  <span className="truncate">
                    {c.name ?? 'Untitled company'}
                  </span>
                  {c.id === activeId ? <CheckIcon /> : null}
                </DropdownMenu.Item>
              ))}
            </>
          ) : null}

          <DropdownMenu.Separator className="mx-2 my-1 h-px bg-border" />
          <DropdownMenu.Item asChild>
            <Link
              to="/settings/account"
              className="flex cursor-pointer items-center rounded-md px-3 py-2 text-body-sm text-text-primary outline-none data-[highlighted]:bg-background"
            >
              Account settings
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={onSignOut}
            className="flex cursor-pointer items-center rounded-md px-3 py-2 text-body-sm text-text-primary outline-none data-[highlighted]:bg-background"
          >
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m3 7 3 3 5-6" />
    </svg>
  );
}

function getInitials(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }
  const single = parts[0]!;
  return single.slice(0, 2).toUpperCase();
}
